<script lang="ts">
  import { onMount } from 'svelte';
  import { api, ApiError, type DirectoryUser, type PublicChannel } from '../lib/api';
  import { conversationPath, router } from '../lib/router.svelte';
  import { app } from '../lib/stores.svelte';
  import Avatar from './Avatar.svelte';

  let { onClose }: { onClose: () => void } = $props();

  let tab = $state<'people' | 'channel' | 'join'>('people');
  let users = $state<DirectoryUser[]>([]);
  let channels = $state<PublicChannel[]>([]);
  let filter = $state('');
  let selected = $state<string[]>([]);
  let name = $state('');
  let topic = $state('');
  let isPrivate = $state(false);
  let error = $state('');
  let busy = $state(false);
  let dialog: HTMLDialogElement | undefined = $state();

  onMount(() => {
    dialog?.showModal();
    void api.users().then((list) => (users = list.filter((u) => u.id !== app.me?.id && !u.is_bot)));
    void api.browse().then((list) => (channels = list));
  });

  const shown = $derived(
    users.filter((u) =>
      `${u.display_name} ${u.username}`.toLowerCase().includes(filter.trim().toLowerCase()),
    ),
  );

  async function run(action: () => Promise<string>) {
    error = '';
    busy = true;
    try {
      const id = await action();
      onClose();
      router.go(conversationPath(id));
    } catch (e) {
      error = e instanceof ApiError ? e.message : 'Une erreur est survenue';
    } finally {
      busy = false;
    }
  }

  const openPeople = () =>
    run(async () => {
      const kind = selected.length === 1 ? 'direct' : 'group';
      const conversation = await api.createConversation({ kind, member_ids: selected });
      app.upsertConversation(conversation);
      return conversation.id;
    });

  const createChannel = () =>
    run(async () => {
      const conversation = await api.createConversation({
        kind: isPrivate ? 'private' : 'public',
        name,
        topic,
      });
      app.upsertConversation(conversation);
      return conversation.id;
    });

  const join = (channel: PublicChannel) =>
    run(async () => {
      if (!app.me) throw new Error();
      app.upsertConversation(await api.addMembers(channel.id, [app.me.id]));
      return channel.id;
    });
</script>

<dialog bind:this={dialog} onclose={onClose} aria-labelledby="new-title">
  <header>
    <h2 id="new-title">Nouvelle conversation</h2>
    <button type="button" class="close" aria-label="Fermer" onclick={() => dialog?.close()}>×</button>
  </header>
  <div class="tabs" role="tablist">
    <button type="button" role="tab" aria-selected={tab === 'people'} onclick={() => (tab = 'people')}>Message direct</button>
    <button type="button" role="tab" aria-selected={tab === 'channel'} onclick={() => (tab = 'channel')}>Nouveau canal</button>
    <button type="button" role="tab" aria-selected={tab === 'join'} onclick={() => (tab = 'join')}>Rejoindre un canal</button>
  </div>

  {#if tab === 'people'}
    <form
      onsubmit={(e) => {
        e.preventDefault();
        void openPeople();
      }}
    >
      <label class="sr-only" for="people-filter">Chercher une personne</label>
      <input id="people-filter" class="input" placeholder="Chercher une personne" bind:value={filter} />
      <ul class="list">
        {#each shown as user (user.id)}
          <li>
            <label class="row">
              <input type="checkbox" value={user.id} bind:group={selected} />
              <Avatar {user} size={28} presence={app.presence[user.id]} />
              <span class="row-text">{user.display_name} <span class="muted">@{user.username}</span></span>
            </label>
          </li>
        {/each}
      </ul>
      <p class="hint">Une personne : message direct. Plusieurs : groupe.</p>
      <button type="submit" class="btn primary" disabled={busy || selected.length === 0}>Ouvrir la conversation</button>
    </form>
  {:else if tab === 'channel'}
    <form
      onsubmit={(e) => {
        e.preventDefault();
        void createChannel();
      }}
    >
      <div class="field">
        <label for="channel-name">Nom du canal</label>
        <input id="channel-name" class="input" required placeholder="support-clients" bind:value={name} />
        <span class="hint">Minuscules, chiffres, « - » et « _ », sans espace.</span>
      </div>
      <div class="field">
        <label for="channel-topic">Sujet (facultatif)</label>
        <input id="channel-topic" class="input" bind:value={topic} />
      </div>
      <label class="row"><input type="checkbox" bind:checked={isPrivate} /> Canal privé, visible seulement par ses membres</label>
      <button type="submit" class="btn primary" disabled={busy || !name.trim()}>Créer le canal</button>
    </form>
  {:else}
    <ul class="list">
      {#each channels as channel (channel.id)}
        <li class="row">
          <span class="row-text">
            <strong># {channel.name}</strong>
            <span class="muted">{channel.topic || 'Sans sujet'} · {channel.member_count} membre{channel.member_count > 1 ? 's' : ''}</span>
          </span>
          <button type="button" class="btn" disabled={busy} onclick={() => join(channel)}>Rejoindre</button>
        </li>
      {:else}
        <li class="hint">Vous êtes déjà dans tous les canaux publics.</li>
      {/each}
    </ul>
  {/if}

  {#if error}<p class="error" role="alert">{error}</p>{/if}
</dialog>

<style>
  dialog {
    width: min(480px, calc(100vw - 32px));
    max-height: calc(100vh - 64px);
    border: 1px solid var(--border);
    border-radius: var(--radius-l);
    padding: 20px;
    background: var(--bg);
    color: var(--ink);
  }
  dialog::backdrop {
    background: rgba(16, 24, 40, 0.4);
  }
  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  h2 {
    margin: 0;
    font-size: 18px;
  }
  .close {
    width: 32px;
    height: 32px;
    border: 0;
    background: none;
    font-size: 22px;
    color: var(--muted);
  }
  .tabs {
    display: flex;
    gap: 4px;
    margin: 12px 0 16px;
    border-bottom: 1px solid var(--border);
  }
  .tabs button {
    height: 36px;
    padding: 0 10px;
    border: 0;
    border-bottom: 2px solid transparent;
    background: none;
    color: var(--muted);
    font-weight: 500;
  }
  .tabs button[aria-selected='true'] {
    color: var(--ink);
    border-color: var(--accent);
    font-weight: 600;
  }
  form {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .list {
    list-style: none;
    margin: 0;
    padding: 0;
    max-height: 320px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .row {
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 40px;
    padding: 4px 6px;
    border-radius: var(--radius-s);
  }
  label.row:hover {
    background: var(--bg-hover);
  }
  .row-text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
  }
  .muted,
  .hint {
    color: var(--muted);
    font-size: 13px;
    margin: 0;
  }
  .error {
    margin-top: 12px;
  }
</style>
